'use client'

import { makeDrupalMenuLinkContent } from '@/lib/utils';
import { DrupalMenuLinkContent, DrupalNode } from 'next-drupal';
import React, { ReactNode, Suspense, useEffect, useState } from 'react';
import { Menu } from './Menu';
import lunr from 'lunr';
import Mark from 'mark.js';
import { usePathname } from 'next/navigation';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { getSidebarMenu } from '@/lib/get-menus';
import { useSelector } from 'react-redux';
import { AppState } from '@/redux/reducer';
import { useVersion } from '@/app/[...slug]/_lib/util';
import { SearchHighlight } from './SearchHighlight';

interface SidebarMenuSearchProps {
  resource: DrupalNode
  product: string
  defaultMenu: DrupalMenuLinkContent[]
  children?: ReactNode
  versions?: string[]
}

export const SidebarMenuSearch = (props: SidebarMenuSearchProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const pathname = usePathname();
  // const menus = useRef<DrupalMenuLinkContent[]>(props.defaultMenu)
  const [menus, setMenu] = useState(props.defaultMenu)

  const versionChoice = useVersion(
      useSelector<AppState, string>((state: AppState) => {
        return state.versionPreferences[props.product]
      }),
      props.versions ?? []
  )

  useEffect(() => {
    if (versionChoice !== null) {
      getSidebarMenu(props.resource, versionChoice).then((menu: DrupalMenuLinkContent[]) => {
        // menus.set = menu
        setMenu(menu)
      })
    }
  }, [versionChoice, props.resource])

  // Creating lunr index which will be searched from the query
  const index = lunr(function () {
    this.field("title");
    this.field("description");
    this.ref("id");
    this.metadataWhitelist = ['position'];

    const createMenuIndex = (menus: DrupalMenuLinkContent[]) => {
      menus.forEach((link) => {
        this.add({
          id: link.id,
          title: link.title,
          description: link.description,
        });

        if (link.items && link.items.length > 0) {
          createMenuIndex(link.items ?? []);
        }
      })
    }

    // Indexing only the contents which needs to searched
    createMenuIndex(menus);
  });

  // store is the data which will be displayed after a search is matched with index.
  const store: {
    [key: string]: DrupalMenuLinkContent
  } = {}
  const createStore = (menus: DrupalMenuLinkContent[]) => {
    menus.forEach((sidebar) => {
      store[sidebar.id] = sidebar
      if (sidebar.items && sidebar.items.length > 0) {
        createStore(sidebar.items ?? []);
      }
    })
  }

  createStore(menus ?? []);

  const results: DrupalMenuLinkContent[] =index.query(function (q) {
    // q.term(lunr.tokenizer(query), {
    //   wildcard: lunr.Query.wildcard.TRAILING | lunr.Query.wildcard.LEADING
    // })
    q.term(searchQuery, {
      wildcard: lunr.Query.wildcard.TRAILING | lunr.Query.wildcard.LEADING,
    })
    q.term(searchQuery, {
      editDistance: 1
    })
  }).map((r) => store[r.ref]);


  // Clear search when navigation changes.
  useEffect(() => {
    setSearchQuery('');
  }, [pathname]);

  return (
    <div className="pr-12">
      <input placeholder="Search menu" onChange={(event) => { setSearchQuery(event.target.value) }} type="text" className="print:hidden w-full p-3 mb-2 rounded border-gray-500 border placeholder-gray-600" id="sidebar-search" />
      {props.children}
      <button className={`print:hidden bg-transparent text-blue-700 py-1 px-2 mb-2 font-semibold hover:underline ${searchQuery == '' ? 'hidden' : ''}`} onClick={() => setSearchQuery('')}>
        <small className="flex"><ChevronLeftIcon className='mr-1 w-4 overflow-visible' /> Back to menu</small>
      </button>
      <Suspense fallback={<></>}>
        <SearchHighlight query={searchQuery}>
          <Menu menu={searchQuery == '' ? menus : results} className="flex flex-col text-navy-600" id="sidebar-menu" query={searchQuery} />
        </SearchHighlight>
      </Suspense>
    </div>
  )
}